import React, { useState, useEffect, useCallback } from 'react';
import { temperatureDataAPI, powerLocationAPI, inverterRealTimeAPI, panelConfigAPI, channelDataAPI, weatherAPI } from '../../services/api';
import usePeriodicFetch from '../../hooks/usePeriodicFetch';
import { WiThermometer, WiHumidity, WiStrongWind } from 'react-icons/wi';
import { BsSunFill, BsCloudSunFill, BsCloudFill, BsCloudRainFill, BsCloudSnowFill, BsCloudDrizzleFill } from 'react-icons/bs';
import './PowerPlantDetailHome.css';

// 날씨 상태별 아이콘 반환 함수
const getWeatherIcon = (weatherStatus) => {
  if (!weatherStatus) return null;
  
  const status = weatherStatus.trim();
  
  switch (status) {
    case '맑음':
      return <BsSunFill className="weather-icon weather-icon-sunny" />;
    case '구름 조금':
      return <BsCloudSunFill className="weather-icon weather-icon-partly-cloudy" />;
    case '구름 많음':
      return <BsCloudFill className="weather-icon weather-icon-cloudy" />;
    case '흐림':
      return <BsCloudFill className="weather-icon weather-icon-overcast" />;
    case '비':
      return <BsCloudRainFill className="weather-icon weather-icon-rain" />;
    case '비/눈':
      return <BsCloudRainFill className="weather-icon weather-icon-sleet" />;
    case '눈':
      return <BsCloudSnowFill className="weather-icon weather-icon-snow" />;
    case '소나기':
      return <BsCloudDrizzleFill className="weather-icon weather-icon-shower" />;
    case '빗방울':
      return <BsCloudDrizzleFill className="weather-icon weather-icon-drizzle" />;
    case '빗방울눈날림':
    case '빗방울날림':
      return <BsCloudRainFill className="weather-icon weather-icon-rain-snow" />;
    case '눈날림':
      return <BsCloudSnowFill className="weather-icon weather-icon-snow-wind" />;
    default:
      return null;
  }
};

// 태양광 테마 가로 막대바 게이지 차트 (현재 전력용)
const SolarPowerChart = ({ value = 0, capacity = 450, title = "현재 전력" }) => {
  const percentage = Math.min(value / capacity, 1) * 100;

  return (
    <div className="solar-power-chart-container">
      <div className="solar-bar-chart-wrapper">
        {/* 맨 위: 미니멀 게이지바 */}
        <div className="solar-bar-gauge-container">
          <div className="solar-minimal-gauge-wrapper">
            <div className="solar-minimal-gauge-bg">
              {/* 채워지는 부분 */}
              <div 
                className="solar-minimal-gauge-fill"
                style={{
                  width: `${percentage}%`
                }}
              />
              
              {/* 퍼센트 텍스트 (게이지 내부) */}
              <div className="solar-minimal-percentage">
                {percentage.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// 발전량 단위 자동 변환 함수 (kWh 기준)
const formatPowerGeneration = (valueInKWh) => {
  if (valueInKWh >= 1000000000) {
    // 1,000,000,000 kWh 이상 → TWh (테라와트시)
    return {
      value: (valueInKWh / 1000000000).toFixed(3),
      unit: 'TWh'
    };
  } else if (valueInKWh >= 1000000) {
    // 1,000,000 kWh 이상 → GWh (기가와트시)
    return {
      value: (valueInKWh / 1000000).toFixed(3),
      unit: 'GWh'
    };
  } else if (valueInKWh >= 1000) {
    // 1,000 kWh 이상 → MWh (메가와트시)
    return {
      value: (valueInKWh / 1000).toFixed(3),
      unit: 'MWh'
    };
  } else {
    // 1,000 kWh 미만 → kWh (킬로와트시)
    return {
      value: Math.round(valueInKWh).toString(),
      unit: 'kWh'
    };
  }
};

const PowerPlantDetailHome = ({ powerPlant, configData }) => {
  const [environmentData, setEnvironmentData] = useState(null);
  const [powerPlantInfo, setPowerPlantInfo] = useState(null);
  const [inverterRealTimeData, setInverterRealTimeData] = useState([]);
  const [inverterListLoading, setInverterListLoading] = useState(true);
  const [serverTime, setServerTime] = useState(null);
  const [generationTime, setGenerationTime] = useState(null);
  const [panelConfigCount, setPanelConfigCount] = useState(null);
  const [panelChannelCount, setPanelChannelCount] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // 발전소 기본 정보 가져오기 (한 번만 실행)
  const fetchPowerPlantInfo = useCallback(async (powerLocationId) => {
    try {
      console.log('[PowerPlantDetailHome] 발전소 정보 요청 중...', { 
        powerLocationId,
        url: `/api/power-locations/${powerLocationId}`
      });
      const data = await powerLocationAPI.get(powerLocationId);
      console.log('[PowerPlantDetailHome] 발전소 정보 받음:', data);
      setPowerPlantInfo(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 발전소 정보 가져오기 실패:', error);
      console.error('에러 상세:', error.response?.data);
      console.error('에러 상태:', error.response?.status);
      setPowerPlantInfo(null);
    }
  }, []);

  // 환경 데이터 가져오기 (주기적 실행)
  const fetchEnvironmentData = useCallback(async (powerLocationId) => {
    try {
      const data = await temperatureDataAPI.getTemperatureData(powerLocationId);
      console.log('[PowerPlantDetailHome] 환경 데이터 응답:', data);
      setEnvironmentData(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 환경 데이터 가져오기 실패:', error);
      setEnvironmentData(null);
    }
  }, []);

  // 인버터 실시간 데이터 가져오기 (주기적 실행)
  const fetchInverterRealTimeData = useCallback(async (powerLocationId) => {
    try {
      const { serverTime, items } = await inverterRealTimeAPI.getInverterRealTimeList(powerLocationId);
      const list = items || [];

      // 서버 시각 기준으로 신선도 판정 (PC 시계 오차 영향 없음)
      const referenceTime = serverTime ? new Date(serverTime) : null;
      list.forEach((inv) => {
        if (inv.updatedAt && referenceTime && !Number.isNaN(referenceTime.getTime())) {
          const updateTime = new Date(inv.updatedAt);
          const diffInSeconds = Math.floor((referenceTime - updateTime) / 1000);
          if (diffInSeconds >= 180) {
            console.warn(`[PowerPlantDetailHome] 오래된 데이터 감지 - 인버터 ID: ${inv.id}, 서버 기준 ${diffInSeconds}초 전`);
          }
        }
      });

      setServerTime(serverTime || null);
      setInverterRealTimeData(list);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 인버터 실시간 데이터 가져오기 실패:', error);
      setServerTime(null);
      setInverterRealTimeData([]);
    } finally {
      setInverterListLoading(false);
    }
  }, []);

  // 발전 시간 정보 가져오기 (주기적 실행)
  const fetchGenerationTime = useCallback(async (powerLocationId) => {
    try {
      const data = await powerLocationAPI.getGenerationTime(powerLocationId);
      setGenerationTime(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 발전 시간 정보 가져오기 실패:', error);
      setGenerationTime(null);
    }
  }, []);

  // 접속반 개수 가져오기 (한 번만 실행)
  const fetchPanelConfigCount = useCallback(async (powerLocationId) => {
    try {
      console.log('[PowerPlantDetailHome] 접속반 개수 요청 중...', { 
        powerLocationId,
        url: `/api/panel-configs/count/${powerLocationId}`
      });
      const data = await panelConfigAPI.getCountByPowerLocation(powerLocationId);
      console.log('[PowerPlantDetailHome] 접속반 개수 받음:', data);
      setPanelConfigCount(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 접속반 개수 가져오기 실패:', error);
      console.error('에러 상세:', error.response?.data);
      console.error('에러 상태:', error.response?.status);
      setPanelConfigCount(null);
    }
  }, []);

  // 정상 접속반 및 채널 개수 가져오기 (주기적 실행)
  const fetchPanelChannelCount = useCallback(async (powerLocationId) => {
    try {
      const data = await channelDataAPI.getCountByPowerLocation(powerLocationId);
      setPanelChannelCount(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 정상 접속반/채널 개수 가져오기 실패:', error);
      setPanelChannelCount(null);
    }
  }, []);

  // 날씨 정보 가져오기 (주기적 실행)
  const fetchWeatherData = useCallback(async (powerLocationId) => {
    try {
      setWeatherLoading(true);
      const data = await weatherAPI.getWeather(powerLocationId);
      setWeatherData(data);
    } catch (error) {
      console.error('[PowerPlantDetailHome] 날씨 정보 가져오기 실패:', error);
      setWeatherData(null);
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    setInverterRealTimeData([]);
    setServerTime(null);
    setInverterListLoading(true);
  }, [powerPlant?.id]);

  // 주기적으로 실행할 모든 데이터 가져오기 (10초 간격)
  const fetchAllPeriodicData = useCallback(async () => {
    if (!powerPlant?.id) return;
    
    console.log('[PowerPlantDetailHome] 주기적 데이터 가져오기 시작');
    
    // 병렬로 모든 데이터 가져오기 (날씨 제외)
    await Promise.all([
      fetchEnvironmentData(powerPlant.id),
      fetchInverterRealTimeData(powerPlant.id),
      fetchGenerationTime(powerPlant.id),
      fetchPanelChannelCount(powerPlant.id)
    ]);
    
    console.log('[PowerPlantDetailHome] 주기적 데이터 가져오기 완료');
  }, [powerPlant?.id, fetchEnvironmentData, fetchInverterRealTimeData, fetchGenerationTime, fetchPanelChannelCount]);

  // 10초 간격으로 주기적 데이터 가져오기
  usePeriodicFetch(
    fetchAllPeriodicData,
    10000, // 10초
    [powerPlant?.id],
    !!powerPlant?.id // powerPlant.id가 있을 때만 활성화
  );

  // 날씨 데이터는 20분 간격으로 가져오기
  usePeriodicFetch(
    () => fetchWeatherData(powerPlant?.id),
    1200000, // 20분 = 1,200,000ms
    [powerPlant?.id],
    !!powerPlant?.id // powerPlant.id가 있을 때만 활성화
  );


  // 발전소 기본 정보 및 접속반 개수, 발전 시간, 날씨 정보 가져오기 (컴포넌트 마운트 시 한 번만 실행)
  useEffect(() => {
    if (powerPlant?.id) {
      fetchPowerPlantInfo(powerPlant.id);
      fetchPanelConfigCount(powerPlant.id);
      fetchGenerationTime(powerPlant.id);
      fetchWeatherData(powerPlant.id);
    }
  }, [powerPlant?.id, fetchPowerPlantInfo, fetchPanelConfigCount, fetchGenerationTime, fetchWeatherData]);

  // 시간 실시간 업데이트 (1초마다)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#10B981';
      case 'partial_error': return '#F59E0B';
      case 'communication_error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  // const getStatusText = (status) => {
  //   switch (status) {
  //     case 'normal': return '정상';
  //     case 'partial_error': return '부분 오류';
  //     case 'communication_error': return '통신 오류';
  //     default: return '알 수 없음';
  //   }
  // };

  // 설비 용량 (capacity) - 백엔드에서 받은 값만 사용
  const capacity = powerPlantInfo?.capacity || 0; // 설비용량 (kW)

  // updatedAt이 서버 시각 기준 3분 이내인지 확인 (PC 시계 사용 안 함)
  const isDataFresh = (updatedAt) => {
    if (!updatedAt) return false;
    // 구버전 API(serverTime 없음)에서는 값을 그대로 표시
    if (!serverTime) return true;
    const referenceTime = new Date(serverTime);
    const updateTime = new Date(updatedAt);
    if (Number.isNaN(referenceTime.getTime()) || Number.isNaN(updateTime.getTime())) return true;
    const diffInMs = referenceTime - updateTime;
    const threeMinutesInMs = 3 * 60 * 1000;
    return diffInMs < threeMinutesInMs;
  };

  // 통신(계통) 정상 값 — 서버 NORMAL_LINE_POWER_LOSS_STATES 와 맞춤
  const isLinePowerNormal = (state) => {
    if (state === null || state === undefined) return false;
    const normalized = String(state).trim().toUpperCase();
    return normalized === 'GOOD'
      || normalized === '정상'
      || normalized === '0'
      || normalized === 'OK'
      || normalized === 'NORMAL';
  };

  // 발전 상태(runState) 표시/판정 유틸
  const isRunState = (runState) => {
    if (runState === 1 || runState === '1') return true;
    if (runState === '정상') return true;
    if (typeof runState === 'string') {
      const normalized = runState.trim().toUpperCase();
      // 'run' 도 toUpperCase()로 'RUN' 처리됨
      if (normalized === 'RUN') return true;
    }
    return false;
  };

  const getRunStateText = (runState) => {
    if (isRunState(runState)) return '정상';
    return '정지';
  };


  // 인버터 실시간 데이터를 기반으로 계산
  const inverterStats = {
    // 전체 인버터 수
    total: inverterRealTimeData.length,
    // 통신 정상: linePowerLossState가 "GOOD" 또는 "정상"이고 updatedAt이 3분 이내인 인버터 수
    communicationNormal: inverterRealTimeData.filter(inv => 
      isLinePowerNormal(inv.linePowerLossState) && isDataFresh(inv.updatedAt)
    ).length,
    // 발전 정상: runState가 1 또는 "RUN"이고 updatedAt이 3분 이내인 인버터 수
    generationNormal: inverterRealTimeData.filter(inv => 
      isRunState(inv.runState) && isDataFresh(inv.updatedAt)
    ).length,
    // 현재 전력 (kW): 모든 인버터의 instantPower 합계 (오래된 데이터는 0으로 계산)
    currentPower: inverterRealTimeData.reduce((sum, inv) => {
      const isFresh = isDataFresh(inv.updatedAt);
      return sum + (isFresh ? (inv.instantPower || 0) : 0);
    }, 0),
    // 일일 발전량 (kWh): 모든 인버터의 dailyCumulativePower 합계
    dailyGeneration: inverterRealTimeData.reduce((sum, inv) => sum + (inv.dailyCumulativePower || 0), 0),
    // 월 발전량 (kWh): 모든 인버터의 monthlyCumulativePower 합계
    monthlyGeneration: inverterRealTimeData.reduce((sum, inv) => sum + (inv.monthlyCumulativePower || 0), 0),
    // 년 발전량 (kWh): 모든 인버터의 yearlyCumulativePower 합계
    yearlyGeneration: inverterRealTimeData.reduce((sum, inv) => sum + (inv.yearlyCumulativePower || 0), 0),
    // 누적 발전량 (kWh): 모든 인버터의 cumulativePower 합계
    totalGeneration: inverterRealTimeData.reduce((sum, inv) => sum + (inv.cumulativePower || 0), 0)
  };

  // 현재 전력 데이터 (게이지용) - API 데이터만 사용
  const currentPower = inverterStats.total > 0 ? inverterStats.currentPower : 0;
  
  // 일일 발전량 (kWh 기준, 자동 단위 변환)
  const dailyPowerRaw = inverterStats.total > 0 ? inverterStats.dailyGeneration : 0;
  const dailyPower = formatPowerGeneration(dailyPowerRaw);
  
  // 월 발전량 (kWh 기준, 자동 단위 변환)
  const monthlyPowerRaw = inverterStats.total > 0 ? inverterStats.monthlyGeneration : 0;
  const monthlyPower = formatPowerGeneration(monthlyPowerRaw);
  
  // 년 발전량 (kWh 기준, 자동 단위 변환)
  const yearlyPowerRaw = inverterStats.total > 0 ? inverterStats.yearlyGeneration : 0;
  const yearlyPower = formatPowerGeneration(yearlyPowerRaw);

  // 총 누적 발전량 (kWh 기준, 자동 단위 변환)
  const totalCumulativeRaw = inverterStats.total > 0 ? inverterStats.totalGeneration : 0;
  const totalCumulative = formatPowerGeneration(totalCumulativeRaw);

  return (
    <div className="power-plant-detail-home">
      <div className="detail-content">
        {/* 왼쪽 패널 (1/3) */}
        <div className="detail-home-left-panel">
          {/* 발전소 기본 정보 */}
          <div className="plant-info-card">
            <h3>발전소 정보</h3>
            <div className="info-section">
              {/* 발전소명과 용량 - 상단 강조 */}
              <div className="info-main">
                <div className="info-item-large">
                  <span className="info-value-large">
                    {powerPlantInfo?.locationName || '-'}
                  </span>
                </div>
                <div className="info-item-large">
                  <span className="info-label-large">설비용량</span>
                  <span className="info-value-large">{capacity}kW</span>
                </div>
              </div>
              
              {/* 상세 주소 */}
              <div className="info-location">
                <div className="info-item">
                  <span className="info-label">상세주소</span>
                  <span className="info-value">
                    {powerPlantInfo?.location || '-'}
                  </span>
                </div>
              </div>
              
              {/* 시간 - 한 행 전체 */}
              <div className="info-time">
                <div className="info-item">
                  <span className="info-value">시간 | {currentTime.toLocaleTimeString()}</span>
                </div>
              </div>

              {/* 기상청 날씨 정보 */}
              <div className="info-weather">
                <div className="info-weather-header">
                  <span className="info-label">오늘 날씨 정보</span>
                </div>
                <div className="info-weather-grid">
                  <div className="info-weather-item">
                    <span className="info-weather-label">현재 온도</span>
                    <span className="info-weather-value">
                      {weatherLoading ? (
                        <span className="weather-loading-spinner"></span>
                      ) : (
                        weatherData?.temperature !== null && weatherData?.temperature !== undefined ? (
                          <span className="weather-status-with-icon">
                            <WiThermometer className="weather-icon weather-icon-temperature" />
                            <span className="weather-status-text">{weatherData.temperature.toFixed(1)}°C</span>
                          </span>
                        ) : '-'
                      )}
                    </span>
                  </div>
                  <div className="info-weather-item">
                    <span className="info-weather-label">날씨 상태</span>
                    <span className="info-weather-value">
                      {weatherLoading ? (
                        <span className="weather-loading-spinner"></span>
                      ) : (
                        weatherData?.weatherStatus ? (
                          <span className="weather-status-with-icon">
                            {getWeatherIcon(weatherData.weatherStatus)}
                            <span className="weather-status-text">{weatherData.weatherStatus}</span>
                          </span>
                        ) : '-'
                      )}
                    </span>
                  </div>
                  <div className="info-weather-item">
                    <span className="info-weather-label">현재 습도</span>
                    <span className="info-weather-value">
                      {weatherLoading ? (
                        <span className="weather-loading-spinner"></span>
                      ) : (
                        weatherData?.humidity !== null && weatherData?.humidity !== undefined ? (
                          <span className="weather-status-with-icon">
                            <WiHumidity className="weather-icon weather-icon-humidity" />
                            <span className="weather-status-text">{weatherData.humidity.toFixed(0)}%</span>
                          </span>
                        ) : '-'
                      )}
                    </span>
                  </div>
                  <div className="info-weather-item">
                    <span className="info-weather-label">풍속</span>
                    <span className="info-weather-value">
                      {weatherLoading ? (
                        <span className="weather-loading-spinner"></span>
                      ) : (
                        weatherData?.windSpeed !== null && weatherData?.windSpeed !== undefined ? (
                          <span className="weather-status-with-icon">
                            <WiStrongWind className="weather-icon weather-icon-wind" />
                            <span className="weather-status-text">{weatherData.windSpeed.toFixed(1)} m/s</span>
                          </span>
                        ) : '-'
                      )}
                    </span>
                  </div>
                </div>
                <div className="info-weather-footer">
                  <img src="/KMA-logo.png" alt="기상청" className="kma-logo" />
                  <span className="kma-provided-text">제공</span>
                </div>
              </div>
            </div>
          </div>

          {/* 접속반 상태 - 데이터가 있을 때만 표시 */}
          {panelConfigCount && panelConfigCount.totalPanelConfigCount > 0 && (
            <div className="plant-info-card">
              <h3>접속반 상태</h3>
              <div className="info-section info-two-columns">
                <div className="info-item">
                  <span className="info-label">접속반</span>
                  <span className="info-value" style={{ 
                    color: panelChannelCount && panelChannelCount.recentPanelDataCount === panelConfigCount.totalPanelConfigCount 
                      ? '#10B981' 
                      : panelChannelCount && panelChannelCount.recentPanelDataCount > 0 
                        ? '#F59E0B' 
                        : '#EF4444'
                  }}>
                    {panelChannelCount 
                      ? `${panelChannelCount.recentPanelDataCount}/${panelConfigCount.totalPanelConfigCount}`
                      : '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">채널</span>
                  <span className="info-value" style={{ 
                    color: panelChannelCount && panelChannelCount.recentChannelDataCount === panelConfigCount.totalChannelCount 
                      ? '#10B981' 
                      : panelChannelCount && panelChannelCount.recentChannelDataCount > 0 
                        ? '#F59E0B' 
                        : '#EF4444'
                  }}>
                    {panelChannelCount 
                      ? `${panelChannelCount.recentChannelDataCount}/${panelConfigCount.totalChannelCount}`
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* 오른쪽 패널 (2/3) */}
        <div className="detail-home-right-panel">
          {/* 환경 정보 - 오른쪽 상단 작은 위젯 */}
          {configData?.temperatureConfig && environmentData && (
          <div className="environment-widget">
            <div className="env-widget-item">
              <span className="env-widget-label">경사일사량</span>
                <span className="env-widget-value" style={{ color: (environmentData?.tIs1 === null || environmentData?.tIs1 === undefined) ? '#EF4444' : 'inherit' }}>
                  {(environmentData?.tIs1 === null || environmentData?.tIs1 === undefined) ? '고장' : Math.round(environmentData.tIs1)}
                  {(environmentData?.tIs1 !== null && environmentData?.tIs1 !== undefined) && <span className="env-widget-unit">W/m²</span>}
              </span>
            </div>
            <div className="env-widget-item">
              <span className="env-widget-label">수평일사량</span>
                <span className="env-widget-value" style={{ color: (environmentData?.tIs2 === null || environmentData?.tIs2 === undefined) ? '#EF4444' : 'inherit' }}>
                  {(environmentData?.tIs2 === null || environmentData?.tIs2 === undefined) ? '고장' : Math.round(environmentData.tIs2)}
                  {(environmentData?.tIs2 !== null && environmentData?.tIs2 !== undefined) && <span className="env-widget-unit">W/m²</span>}
              </span>
            </div>
            <div className="env-widget-item">
              <span className="env-widget-label">표면온도</span>
                <span className="env-widget-value" style={{ color: (environmentData?.tTemperature1 === null || environmentData?.tTemperature1 === undefined) ? '#EF4444' : 'inherit' }}>
                  {(environmentData?.tTemperature1 === null || environmentData?.tTemperature1 === undefined) ? '고장' : environmentData.tTemperature1.toFixed(1)}
                  {(environmentData?.tTemperature1 !== null && environmentData?.tTemperature1 !== undefined) && <span className="env-widget-unit">°C</span>}
              </span>
            </div>
            <div className="env-widget-item">
              <span className="env-widget-label">외기온도</span>
                <span className="env-widget-value" style={{ color: (environmentData?.tTemperature2 === null || environmentData?.tTemperature2 === undefined) ? '#EF4444' : 'inherit' }}>
                  {(environmentData?.tTemperature2 === null || environmentData?.tTemperature2 === undefined) ? '고장' : environmentData.tTemperature2.toFixed(1)}
                  {(environmentData?.tTemperature2 !== null && environmentData?.tTemperature2 !== undefined) && <span className="env-widget-unit">°C</span>}
              </span>
            </div>
          </div>
          )}
          {/* 첫 번째 행: 현재 전력 (왼쪽) + 일일 발전량 (중간) + 인버터 상태 (오른쪽) */}
          <div className="chart-row">
            <div className="plant-info-card status-card">
              <h3 className="current-power-title">
                <span className="current-power-label">현재 전력</span>
                <span className="current-power-value">
                  {currentPower.toFixed(1)} <span className="current-power-unit">kW</span>
                </span>
              </h3>
              <SolarPowerChart 
                value={currentPower} 
                capacity={capacity}
                title="현재 전력"
              />
            </div>
            <div className="plant-info-card status-card">
              <h3>인버터 상태</h3>
              <div className="info-section info-four-columns">
                <div className="info-item">
                  <span className="info-label">통신 상태</span>
                  <span className="info-value" style={{ 
                    color: getStatusColor(
                      inverterStats.communicationNormal === inverterStats.total ? 'normal' : 'partial_error'
                    ) 
                  }}>
                    {inverterStats.total > 0 
                      ? `${inverterStats.communicationNormal}/${inverterStats.total}` 
                      : '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">발전 상태</span>
                  <span className="info-value" style={{ 
                    color: getStatusColor(
                      inverterStats.generationNormal === inverterStats.total ? 'normal' : 'partial_error'
                    ) 
                  }}>
                    {inverterStats.total > 0 
                      ? `${inverterStats.generationNormal}/${inverterStats.total}` 
                      : '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">전일 발전 시간</span>
                  <span className="info-value" style={{ color: '#10B981' }}>
                    {generationTime?.yesterdayGenerationTime !== null && generationTime?.yesterdayGenerationTime !== undefined
                      ? `${generationTime.yesterdayGenerationTime.toFixed(1)}시간`
                      : '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">평균 발전 시간</span>
                  <span className="info-value" style={{ color: '#10B981' }}>
                    {generationTime?.averageGenerationTime !== null && generationTime?.averageGenerationTime !== undefined
                      ? `${generationTime.averageGenerationTime.toFixed(1)}시간`
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 두 번째 행: 발전량 정보: 일, 월, 년, 누적 (1행 4열) */}
          <div className="chart-row chart-row-four">
            <div className="chart-card">
              <div className="value-display">
                <span className="value-label">일 발전량</span>
                <span className="value-pipe">|</span>
                <div className="current-value">
                  <span className="value-number">{dailyPower.value}</span>
                  <span className="value-unit">{dailyPower.unit}</span>
                </div>
              </div>
            </div>
            <div className="chart-card">
              <div className="value-display">
                <span className="value-label">월 발전량</span>
                <span className="value-pipe">|</span>
                <div className="current-value">
                  <span className="value-number">{monthlyPower.value}</span>
                  <span className="value-unit">{monthlyPower.unit}</span>
                </div>
              </div>
            </div>
            <div className="chart-card">
              <div className="value-display">
                <span className="value-label">년 발전량</span>
                <span className="value-pipe">|</span>
                <div className="current-value">
                  <span className="value-number">{yearlyPower.value}</span>
                  <span className="value-unit">{yearlyPower.unit}</span>
                </div>
              </div>
            </div>
            <div className="chart-card">
              <div className="value-display">
                <span className="value-label">누적 발전량</span>
                <span className="value-pipe">|</span>
                <div className="current-value">
                  <span className="value-number">{totalCumulative.value}</span>
                  <span className="value-unit">{totalCumulative.unit}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 인버터 실시간 데이터 목록 */}
          <div className="inverter-list-section">
            <div className="plant-info-card">
              <h3>인버터 실시간 데이터</h3>
              <div className="inverter-cards-container">
                {inverterListLoading ? (
                  <div className="no-inverter-data inverter-list-loading">
                    <span className="weather-loading-spinner"></span>
                    <span>조회 중...</span>
                  </div>
                ) : inverterRealTimeData.length > 0 ? (
                  <div className="inverter-cards-grid">
                    {inverterRealTimeData.map((inverter) => {
                      const isFresh = isDataFresh(inverter.updatedAt);
                      const commStatus = isLinePowerNormal(inverter.linePowerLossState) && isFresh ? 'normal' : 'communication_error';
                      const genStatus = isRunState(inverter.runState) && isFresh ? 'normal' : 'partial_error';
                      
                      return (
                        <div 
                          key={inverter.id} 
                          className={`inverter-card ${!isFresh ? 'stale-data' : ''}`}
                        >
                          <div className="inverter-card-header">
                            <h4 className="inverter-card-title">
                              {inverter.name || '인버터'} - {inverter.inverterId}
                            </h4>
                          </div>
                          <div className="inverter-card-body">
                            <div className="inverter-card-item">
                              <span className="inverter-card-label">현재 전력</span>
                              <span className="inverter-card-value power-value">
                                {isFresh ? (inverter.instantPower?.toFixed(2) || '0.00') : '0.00'} kW
                              </span>
                            </div>
                            <div className="inverter-card-item">
                              <span className="inverter-card-label">일일 발전량</span>
                              <span className="inverter-card-value">
                                {inverter.dailyCumulativePower !== undefined && inverter.dailyCumulativePower !== null 
                                  ? Math.round(inverter.dailyCumulativePower) 
                                  : '-'} kWh
                              </span>
                            </div>
                            <div className="inverter-card-item">
                              <span className="inverter-card-label">통신 상태</span>
                              <span 
                                className="inverter-card-status"
                                style={{ color: getStatusColor(commStatus) }}
                              >
                                {isFresh ? (inverter.linePowerLossState || '-') : '끊김'}
                              </span>
                            </div>
                            <div className="inverter-card-item">
                              <span className="inverter-card-label">발전 상태</span>
                              <span 
                                className="inverter-card-status"
                                style={{ color: getStatusColor(genStatus) }}
                              >
                                {isFresh ? getRunStateText(inverter.runState) : '정지'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-inverter-data">
                    인버터 데이터가 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerPlantDetailHome;
